'use server'

import { apiRequest, ApiResponse } from '../request'

export type AuthorizationStatus =
    | 'AUTH_DRAFT'
    | 'AUTH_PENDING'
    | 'AUTH_SUBMITTED'
    | 'AUTH_APPROVED'
    | 'AUTH_DENIED'
    | 'AUTH_PARTIAL'
    | 'AUTH_EXPIRED'
    | 'AUTH_CANCELLED'

export type AuthorizationUrgency = 'ROUTINE' | 'URGENT' | 'EMERGENCY'

export interface PriorAuthorization {
    id: number
    companyId: number
    healthPlanId: number
    healthPlan?: {
        id: number
        name: string
        ansCode: string
    }
    clientId: number
    client?: {
        id: number
        name: string
    }
    professionalId?: number
    professional?: {
        id: number
        name: string
    }
    authorizationNumber?: string
    operatorProtocol?: string
    operatorNumber?: string
    cardNumber?: string
    status: AuthorizationStatus
    urgency: AuthorizationUrgency
    requestDate: string
    responseDate?: string
    validFrom?: string
    validUntil?: string
    clinicalIndication?: string
    diagnosis?: string
    diagnosisCode?: string
    totalRequested: number
    totalApproved?: number
    denialReason?: string
    notes?: string
    createdAt: string
    updatedAt: string
    items?: PriorAuthorizationItem[]
    documents?: PriorAuthorizationDocument[]
    communications?: PriorAuthorizationCommunication[]
    _count?: {
        items: number
        documents: number
        communications: number
    }
}

export interface PriorAuthorizationItem {
    id: number
    authorizationId: number
    procedureCode: string
    procedureName: string
    requestedQuantity: number
    approvedQuantity?: number
    unitValue: number
    totalValue: number
    status: 'ITEM_PENDING' | 'ITEM_APPROVED' | 'ITEM_DENIED' | 'ITEM_PARTIAL'
    denialReason?: string
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface PriorAuthorizationDocument {
    id: number
    authorizationId: number
    documentType: string
    fileName: string
    fileUrl: string
    uploadedAt: string
    createdAt: string
}

export interface PriorAuthorizationCommunication {
    id: number
    authorizationId: number
    type: 'REQUEST' | 'RESPONSE' | 'ADDITIONAL_INFO' | 'APPEAL'
    direction: 'SENT' | 'RECEIVED'
    content: string
    attachments?: string[]
    sentAt?: string
    receivedAt?: string
    createdAt: string
}

export interface CreateAuthorizationData {
    healthPlanId: number
    clientId: number
    professionalId?: number
    urgency?: AuthorizationUrgency
    clinicalIndication?: string
    diagnosis?: string
    diagnosisCode?: string
    notes?: string
    items?: Array<{
        procedureCode: string
        procedureName: string
        requestedQuantity: number
        unitValue: number
        notes?: string
    }>
}

export interface UpdateAuthorizationData {
    urgency?: AuthorizationUrgency
    clinicalIndication?: string
    diagnosis?: string
    diagnosisCode?: string
    validFrom?: string
    validUntil?: string
    notes?: string
}

export interface ApproveAuthorizationData {
    authorizationNumber?: string
    operatorProtocol?: string
    operatorNumber?: string
    validFrom?: string
    validUntil?: string
}

export interface AuthorizationFilters {
    healthPlanId?: number
    clientId?: number
    professionalId?: number
    status?: AuthorizationStatus
    urgency?: AuthorizationUrgency
    startDate?: string
    endDate?: string
}

// Authorization CRUD
export async function getAuthorizations(
    filters?: AuthorizationFilters
): Promise<ApiResponse<PriorAuthorization[]>> {
    return apiRequest<PriorAuthorization[]>('billing/authorizations', {
        params: filters,
    })
}

export async function getAuthorization(
    id: number
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(`billing/authorizations/${id}`, {})
}

export async function createAuthorization(
    data: CreateAuthorizationData
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        'billing/authorizations',
        { data },
        'post'
    )
}

export async function updateAuthorization(
    id: number,
    data: UpdateAuthorizationData
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${id}`,
        { data },
        'patch'
    )
}

// Authorization items
export async function addAuthorizationItem(
    authorizationId: number,
    data: {
        procedureCode: string
        procedureName: string
        requestedQuantity: number
        unitValue: number
        notes?: string
    }
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${authorizationId}/items`,
        { data },
        'post'
    )
}

export async function updateAuthorizationItem(
    authorizationId: number,
    itemId: number,
    data: Partial<{
        requestedQuantity: number
        approvedQuantity: number
        unitValue: number
        status: PriorAuthorizationItem['status']
        denialReason: string
        notes: string
    }>
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${authorizationId}/items/${itemId}`,
        { data },
        'patch'
    )
}

export async function removeAuthorizationItem(
    authorizationId: number,
    itemId: number
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${authorizationId}/items/${itemId}`,
        {},
        'delete'
    )
}

// Documents
export async function uploadAuthorizationDocument(
    authorizationId: number,
    data: {
        documentType: string
        fileName: string
        fileUrl: string
    }
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${authorizationId}/documents`,
        { data },
        'post'
    )
}

export async function removeAuthorizationDocument(
    authorizationId: number,
    documentId: number
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${authorizationId}/documents/${documentId}`,
        {},
        'delete'
    )
}

// Workflow
export async function submitAuthorization(
    id: number
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${id}/submit`,
        {},
        'post'
    )
}

export async function approveAuthorization(
    id: number,
    data: ApproveAuthorizationData
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${id}/approve`,
        { data },
        'post'
    )
}

export async function denyAuthorization(
    id: number,
    reason?: string
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${id}/deny`,
        { data: { reason: reason || '' } },
        'post'
    )
}

export async function cancelAuthorization(
    id: number
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${id}/cancel`,
        {},
        'post'
    )
}

export async function expireAuthorization(
    id: number
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${id}/expire`,
        {},
        'post'
    )
}

// Type alias for convenience
export type Authorization = PriorAuthorization

// Communications
export async function addCommunication(
    authorizationId: number,
    data: {
        type: PriorAuthorizationCommunication['type']
        direction: PriorAuthorizationCommunication['direction']
        content: string
        attachments?: string[]
    }
): Promise<ApiResponse<PriorAuthorization>> {
    return apiRequest<PriorAuthorization>(
        `billing/authorizations/${authorizationId}/communications`,
        { data },
        'post'
    )
}

// Labels
export const AUTHORIZATION_STATUS_LABELS: Record<AuthorizationStatus, string> = {
    AUTH_DRAFT: 'Rascunho',
    AUTH_PENDING: 'Pendente',
    AUTH_SUBMITTED: 'Enviada',
    AUTH_APPROVED: 'Aprovada',
    AUTH_DENIED: 'Negada',
    AUTH_PARTIAL: 'Parcialmente Aprovada',
    AUTH_EXPIRED: 'Expirada',
    AUTH_CANCELLED: 'Cancelada',
}

export const AUTHORIZATION_STATUS_COLORS: Record<AuthorizationStatus, string> = {
    AUTH_DRAFT: 'badge-ghost',
    AUTH_PENDING: 'badge-warning',
    AUTH_SUBMITTED: 'badge-info',
    AUTH_APPROVED: 'badge-success',
    AUTH_DENIED: 'badge-error',
    AUTH_PARTIAL: 'badge-warning',
    AUTH_EXPIRED: 'badge-ghost',
    AUTH_CANCELLED: 'badge-ghost',
}

export const AUTHORIZATION_URGENCY_LABELS: Record<AuthorizationUrgency, string> = {
    ROUTINE: 'Rotina',
    URGENT: 'Urgente',
    EMERGENCY: 'Emergencia',
}

export const AUTHORIZATION_URGENCY_COLORS: Record<AuthorizationUrgency, string> = {
    ROUTINE: 'badge-ghost',
    URGENT: 'badge-warning',
    EMERGENCY: 'badge-error',
}

export const ITEM_STATUS_LABELS: Record<PriorAuthorizationItem['status'], string> = {
    ITEM_PENDING: 'Pendente',
    ITEM_APPROVED: 'Aprovado',
    ITEM_DENIED: 'Negado',
    ITEM_PARTIAL: 'Parcial',
}
